// src/repositories/bookamrk.repository.ts
import { safeRequest, AppException } from '../api/safeRequest.ts';
import { toNumber, toStrArray } from '../utils/toNumber.ts';
import { normalizeServiceCard } from '../mappers/service.mapper.ts';
import { BookmarkNetwork } from '../api/bookmark.network.ts';
import { useBookmarkStore } from '../stores/bookmark.store.ts';
import type { BookmarkListResponse } from '../models/bookmark.model.ts';

const PER_PAGE = 20;

function normalizeBookmarkListResponse(res: any): BookmarkListResponse {
  const data = (res as any)?.data ?? res;
  const pg = data?.pagination ?? {};
  return {
    pagination: {
      total_items: toNumber(pg.total_items, 0),
      current_page: toNumber(pg.current_page, 1),
      total_page: toNumber(pg.total_pages ?? pg.total_page, 1),
    },
    bookmarks: (data?.bookmarks ?? []).map(normalizeServiceCard),
  };
}

export const BookmarkRepository = {
  syncBookmarks: async () => {
    const store = useBookmarkStore.getState();
    if (store.syncLoading) return;

    store.setSyncLoading(true);
    try {
      const res = await safeRequest(() => BookmarkNetwork.syncBookmarks(), {
        handleGlobally: false,
      });
      const data = (res as any)?.data ?? res;
      const services: string[] = toStrArray(data?.services);
      store.setSyncedUuids(services);
    } catch {
      // sync 실패는 무시 (북마크 상태가 없는 것으로 처리)
    } finally {
      store.setSyncLoading(false);
    }
  },

  fetchBookmarkList: async ({ page = 1, force = false }: { page?: number; force?: boolean }) => {
    const store = useBookmarkStore.getState();
    if (store.listLoading) return null;

    const isFirstPage = page <= 1;

    store.setListLoading(true);
    store.setListError(null);

    try {
      const res = await safeRequest(
        () => BookmarkNetwork.getBookmarkList(page, PER_PAGE),
        { handleGlobally: false }
      );
      const payload = normalizeBookmarkListResponse(res);
      store.setBookmarkList(payload, !isFirstPage);
      return payload;
    } catch (e: any) {
      const msg =
        e instanceof AppException
          ? e.message
          : e?.response?.data?.message || e?.message || 'Failed to load bookmarks.';
      store.setListError(msg);
      return null;
    } finally {
      store.setListLoading(false);
    }
  },

  toggleBookmark: async (serviceUUID: string) => {
    const store = useBookmarkStore.getState();
    if (store.togglingUuids.has(serviceUUID)) return;

    // 낙관적 업데이트
    store.toggleOptimistic(serviceUUID);
    store.setToggling(serviceUUID, true);

    try {
      await safeRequest(
        () => BookmarkNetwork.toggleBookmark(serviceUUID),
        { handleGlobally: false }
      );
    } catch {
      // 실패 시 롤백
      store.toggleOptimistic(serviceUUID);
    } finally {
      store.setToggling(serviceUUID, false);
    }
  },
};
